import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ScrollView, RefreshControl } from 'react-native';
import { Card, Searchbar, Chip, ActivityIndicator, FAB } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import HelpBanner from '../../components/HelpBanner';

const ESTADOS_CONFIG = {
  pendiente: { label: 'Pendiente', color: '#FFC107', icon: 'time', textColor: '#000' },
  aprobada: { label: 'Aprobada', color: '#17A2B8', icon: 'checkmark-done', textColor: '#fff' },
  pagada: { label: 'Pagada', color: '#28A745', icon: 'checkmark-circle', textColor: '#fff' },
  cancelada: { label: 'Cancelada', color: '#DC3545', icon: 'close-circle', textColor: '#fff' },
};

const ComisionesScreen = () => {
  const navigation = useNavigation();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const [comisiones, setComisiones] = useState([]);
  const [filteredComisiones, setFilteredComisiones] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resumen, setResumen] = useState(null);

  const isAdmin = user && ['admin', 'supervisor', 'gerente'].includes(user.rol);

  useEffect(() => {
    loadData();
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [navigation]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [comisionesData, resumenData] = await Promise.all([
        api.getComisiones(),
        api.getComisionesResumen()
      ]);
      setComisiones(comisionesData);
      setResumen(resumenData);
      applyFilters(comisionesData, searchQuery, filterEstado);
    } catch (error) {
      console.error('Error al cargar comisiones:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery, filterEstado]);

  const applyFilters = (data, query, estado) => {
    let filtered = [...data];
    if (estado !== 'todos') {
      filtered = filtered.filter(c => c.estado === estado);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(c =>
        (c.vendedor_nombre || '').toLowerCase().includes(q) ||
        (c.cliente_nombre || '').toLowerCase().includes(q) ||
        `#${c.venta_id}`.includes(q)
      );
    }
    setFilteredComisiones(filtered);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(comisiones, query, filterEstado);
  };

  const handleFilterChange = (filter) => {
    setFilterEstado(filter);
    applyFilters(comisiones, searchQuery, filter);
  };

  const renderResumen = () => {
    if (!resumen) return null;
    return (
      <View style={styles.resumenContainer}>
        <View style={styles.resumenRow}>
          <View style={[styles.resumenCard, { backgroundColor: '#FFF3CD' }]}>
            <Text style={styles.resumenValue}>L. {parseFloat(resumen.total_pendiente || 0).toFixed(2)}</Text>
            <Text style={styles.resumenLabel}>Pendiente ({resumen.cantidad_pendiente || 0})</Text>
          </View>
          <View style={[styles.resumenCard, { backgroundColor: '#D1ECF1' }]}>
            <Text style={styles.resumenValue}>L. {parseFloat(resumen.total_aprobada || 0).toFixed(2)}</Text>
            <Text style={styles.resumenLabel}>Aprobada ({resumen.cantidad_aprobada || 0})</Text>
          </View>
        </View>
        <View style={[styles.resumenCard, { backgroundColor: '#D4EDDA', marginHorizontal: 8 }]}>
          <Text style={styles.resumenValue}>L. {parseFloat(resumen.pagado_este_mes || 0).toFixed(2)}</Text>
          <Text style={styles.resumenLabel}>Pagado este mes</Text>
        </View>
      </View>
    );
  };

  const renderComision = ({ item }) => {
    const estadoConfig = ESTADOS_CONFIG[item.estado] || ESTADOS_CONFIG.pendiente;
    return (
      <TouchableOpacity onPress={() => navigation.navigate('ComisionDetalle', { comisionId: item.id })}>
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.cardHeader}>
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>Venta #{item.venta_id}</Text>
                <Text style={styles.cardSubtitle}>{item.vendedor_nombre}</Text>
                {item.cliente_nombre && (
                  <Text style={styles.cardSubtitle}>Cliente: {item.cliente_nombre}</Text>
                )}
              </View>
              <View style={styles.cardRight}>
                <Text style={styles.cardAmount}>L. {parseFloat(item.monto_total).toFixed(2)}</Text>
                <View style={[styles.estadoChip, { backgroundColor: estadoConfig.color }]}>
                  <Ionicons name={estadoConfig.icon} size={12} color={estadoConfig.textColor} />
                  <Text style={[styles.estadoText, { color: estadoConfig.textColor }]}>{estadoConfig.label}</Text>
                </View>
              </View>
            </View>
            <Text style={styles.cardDate}>
              {new Date(item.created_at).toLocaleDateString('es-HN')} - Venta total: L. {parseFloat(item.venta_total || 0).toFixed(2)}
            </Text>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
        <Text>Cargando comisiones...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <HelpBanner title="¿Cómo funcionan las comisiones?">
        <Text style={styles.helpText}>
          La comisión se genera <Text style={styles.helpStrong}>automáticamente</Text> al
          completarse una venta, según las reglas configuradas (botón ⚙). Pasa por estos estados:
        </Text>
        <View style={{ marginTop: 6 }}>
          <Text style={styles.helpItem}>
            <Text style={[styles.helpStrong, { color: '#FFC107' }]}>Pendiente</Text> — la comisión se calculó pero aún no fue revisada por un admin.
          </Text>
          <Text style={styles.helpItem}>
            <Text style={[styles.helpStrong, { color: '#17A2B8' }]}>Aprobada</Text> — admin/supervisor la revisó y validó. Ya cuenta para vouchers.
          </Text>
          <Text style={styles.helpItem}>
            <Text style={[styles.helpStrong, { color: '#28A745' }]}>Pagada</Text> — incluida en un voucher emitido y pagado al vendedor.
          </Text>
          <Text style={styles.helpItem}>
            <Text style={[styles.helpStrong, { color: '#DC3545' }]}>Cancelada</Text> — la venta se anuló o admin rechazó la comisión.
          </Text>
        </View>
        {isAdmin && (
          <Text style={[styles.helpText, { marginTop: 8 }]}>
            <Text style={styles.helpStrong}>Botones flotantes:</Text> ⚙ configurar reglas (global, por categoría o por producto), 💵 registrar pago batch a un vendedor, 📊 reporte por período.
          </Text>
        )}
      </HelpBanner>

      {renderResumen()}

      <Searchbar
        placeholder="Buscar por vendedor, cliente o # venta..."
        onChangeText={handleSearch}
        value={searchQuery}
        style={styles.searchBar}
      />

      <View style={styles.filtersContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {['todos', 'pendiente', 'aprobada', 'pagada', 'cancelada'].map(f => (
            <Chip
              key={f}
              onPress={() => handleFilterChange(f)}
              style={[styles.chip, filterEstado === f && styles.chipActive]}
              textStyle={filterEstado === f ? styles.chipTextActive : styles.chipTextInactive}
            >
              {f === 'todos' ? 'Todos' : ESTADOS_CONFIG[f]?.label || f}
            </Chip>
          ))}
        </ScrollView>
      </View>

      <FlatList
        data={filteredComisiones}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderComision}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />}
        ListEmptyComponent={<Text style={styles.emptyText}>No hay comisiones</Text>}
        contentContainerStyle={filteredComisiones.length === 0 ? styles.emptyContainer : undefined}
      />

      {isAdmin && (
        <View style={[styles.fabContainer, { bottom: 16 + insets.bottom }]}>
          <FAB
            style={[styles.fab, { backgroundColor: '#6F42C1', bottom: 70 }]}
            icon="chart-bar"
            small
            onPress={() => navigation.navigate('ComisionReporte')}
          />
          <FAB
            style={[styles.fab, { backgroundColor: '#17A2B8' }]}
            icon="cash-multiple"
            small
            onPress={() => navigation.navigate('PagoComisiones')}
          />
          <FAB
            style={[styles.fabConfig, { backgroundColor: '#007bff' }]}
            icon="cog"
            small
            onPress={() => navigation.navigate('ConfigComisiones')}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  resumenContainer: { padding: 8, paddingBottom: 0 },
  resumenRow: { flexDirection: 'row', marginBottom: 8 },
  resumenCard: {
    flex: 1, padding: 12, borderRadius: 8, marginHorizontal: 4, alignItems: 'center',
  },
  resumenValue: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  resumenLabel: { fontSize: 12, color: '#666', marginTop: 2 },
  searchBar: { margin: 8, elevation: 2 },
  filtersContainer: { paddingHorizontal: 8, marginBottom: 8 },
  chip: { marginRight: 6, backgroundColor: '#f0f0f0' },
  chipActive: { backgroundColor: '#4CAF50' },
  chipTextInactive: { color: '#333' },
  chipTextActive: { color: 'white', fontWeight: '600' },
  card: { marginHorizontal: 8, marginBottom: 8, elevation: 2 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  cardInfo: { flex: 1 },
  cardTitle: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  cardSubtitle: { fontSize: 13, color: '#666', marginTop: 2 },
  cardRight: { alignItems: 'flex-end' },
  cardAmount: { fontSize: 18, fontWeight: 'bold', color: '#28A745' },
  estadoChip: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3,
    borderRadius: 12, marginTop: 4,
  },
  estadoText: { fontSize: 11, fontWeight: '600', marginLeft: 4 },
  cardDate: { fontSize: 12, color: '#999', marginTop: 8 },
  emptyText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 40 },
  emptyContainer: { flex: 1, justifyContent: 'center' },
  fabContainer: { position: 'absolute', right: 16, bottom: 16 },
  fab: { position: 'absolute', right: 0, bottom: 0 },
  fabConfig: { position: 'absolute', right: 0, bottom: 140 },
  helpText: { fontSize: 12, color: '#333', lineHeight: 17 },
  helpStrong: { fontWeight: '700' },
  helpItem: { fontSize: 12, color: '#333', lineHeight: 18, marginTop: 2 },
});

export default ComisionesScreen;
